import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('cart-items:rendered', this.#handleCartItemsRendered);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    window.addEventListener('resize', this.#handleResize);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('cart-items:rendered', this.#handleCartItemsRendered);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    window.removeEventListener('resize', this.#handleResize);
  }

  /** Stillwell: a code applied or removed changes the summary's height; re-place its edge once the new markup is in. */
  #handleDiscountUpdate = (/** @type {any} */ event) => {
    event?.promise
      ?.then(() => requestAnimationFrame(() => this.#themeDrawer?.isOpen && this.#updateStickyState()))
      .catch(() => {});
  };

  #resizeFrame = 0;
  #handleResize = () => {
    if (!this.#themeDrawer?.isOpen) return;
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Stillwell: new cart markup went in ahead of the cart event (CartItemsComponent.renderSectionHtml). A closed
   * drawer measures on open; an open one (desktop, side by side with the page) re-measures now, in the same frame
   * as the new markup (a frame later, the summary showed in one place and then moved to the bottom).
   */
  #handleCartItemsRendered = () => {
    if (this.#themeDrawer?.isOpen) this.#updateStickyState();
  };

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    // Stillwell: opened before it was warmed (snippets/cart-drawer.liquid): its lazy images and installments line now
    /** @type {any} */ (window).slWarmDrawer?.();
    // Stillwell: opened with free shipping already unlocked. Short phone screens leave the "You've unlocked free
    // shipping" pill out then, so the items get its room (snippets/sl-cart-drawer-top.liquid). Read once per opening:
    // while the drawer stays open the strip keeps its height, so the list never jumps under the finger when the
    // stepper takes the cart over or under the free-shipping line.
    this.#themeDrawer?.toggleAttribute('data-sl-ship-unlocked', Boolean(this.querySelector('.sl-cart-top__bar.is-done')));
    // ...and opened without the bar (the One-knee wrap is in the cart: the list offers Both knees instead). The bar
    // stays out until the drawer closes, so switching to Both knees does not bring it back (+51px) and push the new
    // line's stepper under the finger that tapped the offer.
    const top = this.querySelector('.sl-cart-top');
    const ship = top?.querySelector('.sl-cart-top__ship:not(.sl-cart-top__ship--quiet)');
    this.#themeDrawer?.toggleAttribute('data-sl-ship-hidden', Boolean(top && !ship));
    // ...and opened with it (One knee x2): it stays while the stepper takes the cart back to the Both-knees offer
    this.#themeDrawer?.toggleAttribute('data-sl-ship-kept', Boolean(ship));
    // The summary edge is lined up after the first frame of the slide-in (it read every line of the list inside the
    // tap: 123ms of script on a slow phone); it moves by a few pixels at most, while the drawer is still sliding.
    this.#updateStickyState(false);
    requestAnimationFrame(() => setTimeout(() => this.#themeDrawer?.isOpen && this.#updateStickyState()));

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Stillwell: no early open with the old contents (it showed the previous cart, then swapped and animated
    // the new line in ~1s later). The drawer opens once, below, after the new markup is in place.

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          // Stillwell: one microtask later, so cart-items-component (which reacts to the same promise after this
          // listener) has morphed the new markup first; the open then measures the sticky summary on it.
          queueMicrotask(openAndSettle);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /** @param {boolean} [align] - Stillwell: also line up the summary edge (default true) */
  #updateStickyState(align = true) {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    if (summary instanceof HTMLElement) summary.style.removeProperty('--sl-summary-lift');
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    if (align) this.#alignSummaryEdge(dialog, content, summary);
  }

  /**
   * Stillwell: in the first view (list at the top) the summary's top edge never slices a line of text in half. When
   * it would, the summary grows by the few pixels that put its edge just above that line (at most 48px, else the
   * list is left as it is).
   * @param {HTMLDialogElement} dialog
   * @param {Element} content - .cart-drawer__content
   * @param {Element} summary - .cart-drawer__summary
   */
  #alignSummaryEdge(dialog, content, summary) {
    const items = dialog.querySelector('.cart-drawer__items');
    if (!(summary instanceof HTMLElement) || !items || !this.#themeDrawer?.isOpen) return;
    if (getComputedStyle(summary).position !== 'sticky') return;
    const edge = summary.getBoundingClientRect().top;
    // Positions as they are with the list scrolled to the top (phones scroll the content, desktop the items)
    const offset = content.scrollTop + items.scrollTop;
    /** @type {Array<[number, number]>} */
    const lines = [];
    const range = document.createRange();
    // Only the blocks that reach the band the edge can move through (the lift is 48px at most) are read line by line
    const low = edge - 64;
    const high = edge + 2;
    /** @param {Element} el */
    const visit = (el) => {
      for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.nodeValue?.trim()) continue;
          range.selectNodeContents(node);
          for (const box of range.getClientRects()) {
            if (box.height) lines.push([box.top + offset, box.bottom + offset]);
          }
        } else if (node instanceof Element) {
          if (node.matches('.visually-hidden, [hidden], script, style, template')) continue;
          const r = node.getBoundingClientRect();
          // display: contents and the like have no box of their own: their children are looked at
          if (r.width || r.height) {
            if (r.bottom + offset < low || r.top + offset > high) continue;
          }
          visit(node);
        }
      }
    };
    visit(items);
    let lift = 0;
    for (let pass = 0; pass < 4; pass++) {
      const cut = edge - lift;
      const sliced = lines.filter(([top, bottom]) => top < cut - 1 && bottom > cut + 1);
      if (!sliced.length) break;
      lift = edge - Math.min(...sliced.map(([top]) => top)) + 1;
    }
    if (lift > 0 && lift <= 48) summary.style.setProperty('--sl-summary-lift', `${Math.ceil(lift)}px`);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
